/**
 * The theme class contract — the one place that knows how a stored preference becomes a
 * class on the root element.
 *
 * Pure: no DOM, no React, no storage. Two callers depend on it agreeing with itself:
 * the runtime store (`themeStore.ts`) and the pre-paint script (`public/theme-init.js`),
 * which cannot import from this file because it must run before any module loads.
 * `themeContract.test.ts` executes that script and asserts it lands on the same class
 * this module would compute, so a rename here fails a test rather than a screenshot.
 *
 * Only two themes ship (foundation ADR §8). KANON's third block, `.dir-pulse` — light
 * with a mono-ink accent instead of ember — stays in `tokens/colors.css`, unused, so
 * reversing that decision costs nothing. It is deliberately absent from this contract:
 * an unlisted class cannot be selected by accident.
 */

/** Where the trainer's choice is remembered. Mirrored verbatim in `public/theme-init.js`. */
export const THEME_STORAGE_KEY = 'movebuddy.theme';

/** What the trainer picked. `system` follows `prefers-color-scheme`. */
export type ThemePreference = 'light' | 'dark' | 'system';

/** The class KANON puts on the root element. Nothing else in the system changes. */
export type ThemeClass = 'dir-pulseflame' | 'dir-pulseflamedark';

export const THEME_CLASS = {
  light: 'dir-pulseflame',
  dark: 'dir-pulseflamedark',
} as const satisfies Record<'light' | 'dark', ThemeClass>;

/** Every class this app may apply — used to clear the previous one before adding the next. */
export const ALL_THEME_CLASSES: readonly ThemeClass[] = [THEME_CLASS.light, THEME_CLASS.dark];

export const THEME_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

/**
 * Coerce whatever storage held into a preference. Anything unrecognised — absent, empty,
 * a value written by an older build, a value a user typed into devtools — resolves to
 * `system` rather than throwing, because a broken preference must not stop the app
 * rendering.
 */
export function parseThemePreference(raw: string | null | undefined): ThemePreference {
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
}

/** The class to apply, given the preference and what the OS currently asks for. */
export function resolveThemeClass(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ThemeClass {
  if (preference === 'light') return THEME_CLASS.light;
  if (preference === 'dark') return THEME_CLASS.dark;
  return systemPrefersDark ? THEME_CLASS.dark : THEME_CLASS.light;
}
